import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Loader2, Download, Music, Upload, Wallet, CheckCircle2, ChevronDown, Wand2, Mic, FileAudio, XCircle,
} from 'lucide-react';
import { authFetch, postJson, uploadForm, downloadFile, fmtAr, fmtSize, fmtDur } from './utileApi';

// Créer une chanson / transformer une chanson existante. Paiement par le portefeuille TeckiA Utile (Ariary).
const STYLE_CHIPS = ['Pop', 'Afro', 'Rap', 'Gospel', 'Salegy', 'Acoustique', 'Rock', 'Reggae', 'R&B', 'Électro', 'Ballade'];

function Slider({ label, hint, value, onChange }) {
  return (
    <div>
      <div className="flex justify-between text-xs text-blue-200 mb-1"><span>{label}</span><span>{Math.round(value * 100)}%</span></div>
      <input type="range" min="0" max="1" step="0.05" value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full accent-fuchsia-400" />
      {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
    </div>
  );
}

function TopUp({ status, onDone }) {
  const [amount, setAmount] = useState(String(Math.max(status.min_topup_ar, status.song_price_ar * 2)));
  const [method, setMethod] = useState('mvola');
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const info = status.payment[method];

  const submit = async () => {
    setBusy(true); setMsg({ type: '', text: '' });
    const { data } = await postJson('/api/utile/credits/order', { amount_ar: parseInt(amount, 10), method, payment_ref: ref.trim() });
    setBusy(false);
    if (data.success) { setMsg({ type: 'ok', text: "Paiement déclaré ! L'équipe le vérifie ; vous recevrez un message dans votre boîte de réception dès que votre solde sera crédité." }); setRef(''); onDone(); }
    else setMsg({ type: 'error', text: data.message || 'Déclaration impossible.' });
  };

  return (
    <div className="rounded-2xl bg-white/5 border border-fuchsia-500/30 p-5 space-y-4">
      <p className="font-bold flex items-center gap-2"><Wallet className="w-4 h-4 text-fuchsia-300" /> Recharger mon portefeuille TeckiA Utile</p>
      <div>
        <label className="text-xs text-blue-200">Montant (Ariary) — minimum {fmtAr(status.min_topup_ar)}</label>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
          className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-fuchsia-400" />
        <div className="flex gap-2 mt-2 flex-wrap">
          {[1500, 3000, 6000, 15000].map(a => (
            <button key={a} onClick={() => setAmount(String(a))} className="px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs transition">{fmtAr(a)}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(status.payment).map(([key, p]) => (
          <button key={key} onClick={() => setMethod(key)}
            className={`py-2.5 rounded-xl border text-sm font-semibold transition ${method === key ? 'bg-fuchsia-500/25 border-fuchsia-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>{p.label}</button>
        ))}
      </div>
      <div className="rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/20 p-3 text-xs text-fuchsia-100 leading-relaxed">
        1. Envoyez le montant par {info.label} au <b>{info.number}</b> (au nom de <b>{info.name}</b>).<br />
        2. Recopiez ci-dessous la référence de la transaction reçue par SMS.<br />
        3. L'équipe vérifie et crédite votre portefeuille ; vous êtes prévenu dans votre boîte de réception.
      </div>
      <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Référence de la transaction"
        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm placeholder-gray-500 focus:outline-none focus:border-fuchsia-400" />
      <button onClick={submit} disabled={busy || ref.trim().length < 4 || parseInt(amount, 10) < status.min_topup_ar}
        className="w-full py-3 rounded-xl font-bold bg-gradient-to-r from-fuchsia-500 to-purple-500 disabled:opacity-40 transition flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Déclarer mon paiement
      </button>
      {msg.text && <p className={`text-sm rounded-xl px-4 py-3 border ${msg.type === 'ok' ? 'text-emerald-200 bg-emerald-500/10 border-emerald-500/30' : 'text-rose-300 bg-rose-500/10 border-rose-500/30'}`}>{msg.text}</p>}
    </div>
  );
}

function SongCard({ song }) {
  const [err, setErr] = useState('');
  const dl = async (i) => {
    setErr('');
    try { await downloadFile(`/api/utile/songs/${song.id}/download/${i}`, `${song.title || 'chanson'}_v${i + 1}.mp3`); }
    catch (e) { setErr(e.message); }
  };
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold break-all flex items-center gap-2"><Music className="w-4 h-4 text-fuchsia-300 shrink-0" /> {song.title || (song.mode === 'cover' ? 'Reprise' : 'Chanson')}</p>
        <span className="text-[11px] text-gray-400 shrink-0">{song.created_at ? new Date(song.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
      </div>
      {song.status === 'running' && <p className="text-sm text-amber-200 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Création en cours (environ 1 minute)…</p>}
      {song.status === 'failed' && <p className="text-sm text-rose-300 flex items-start gap-2"><XCircle className="w-4 h-4 mt-0.5 shrink-0" /> {song.error} Votre solde a été remboursé.</p>}
      {song.status === 'done' && song.tracks.map((t, i) => (
        <div key={i} className="rounded-xl bg-black/20 p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs text-blue-200">Version {i + 1}{t.duration ? ` · ${fmtDur(t.duration)}` : ''}</span>
            <button onClick={() => dl(i)} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold transition flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> MP3</button>
          </div>
          <audio controls src={t.audio_url} preload="none" className="w-full" />
        </div>
      ))}
      {err && <p className="text-rose-300 text-xs">{err}</p>}
      {song.status === 'done' && <p className="text-[11px] text-gray-400">Les chansons restent disponibles 14 jours : téléchargez celles que vous gardez.</p>}
    </div>
  );
}

export default function SongTool({ status, reload }) {
  const [tab, setTab] = useState('create');          // 'create' | 'cover'
  const [custom, setCustom] = useState(false);       // création : idée simple / personnalisée
  const [f, setF] = useState({ prompt: '', title: '', style: '', lyrics: '', negative_tags: '', model: 'V6_MINI', instrumental: false, vocal_gender: '' });
  const [adv, setAdv] = useState({ style_weight: 0.65, weirdness_constraint: 0.5, audio_weight: 0.65, variety: 1 });
  const [showAdv, setShowAdv] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const [audio, setAudio] = useState(null);
  const [pct, setPct] = useState(null);
  const [songs, setSongs] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const stop = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const loadSongs = useCallback(async () => {
    try {
      const r = await authFetch('/api/utile/songs');
      const d = await r.json();
      if (d.success) { setSongs(d.songs); if (!d.songs.some(s => s.status === 'running')) { stop(); } reload(); return d.songs; }
    } catch (_) { /* réseau : on réessaie au prochain tour */ }
    return null;
  }, [reload]);

  const startPolling = useCallback(() => {
    stop();
    pollRef.current = setInterval(loadSongs, 6000);
  }, [loadSongs]);

  useEffect(() => {
    loadSongs().then(list => { if (list && list.some(s => s.status === 'running')) startPolling(); });
    return stop;
  }, [loadSongs, startPolling]);

  const insufficient = status && status.balance < status.song_price_ar;
  const runningNow = songs.some(s => s.status === 'running');

  const submit = async () => {
    setError(''); setBusy(true);
    const form = new FormData();
    form.append('mode', tab);
    form.append('model', f.model);
    form.append('instrumental', String(f.instrumental));
    if (f.vocal_gender) form.append('vocal_gender', f.vocal_gender);
    if (tab === 'create') {
      form.append('custom', String(custom));
      if (custom) { ['title', 'style', 'lyrics', 'negative_tags'].forEach(k => f[k] && form.append(k, f[k])); }
      else form.append('prompt', f.prompt);
    } else {
      ['title', 'style', 'lyrics', 'negative_tags'].forEach(k => f[k] && form.append(k, f[k]));
      form.append('audio', audio);
    }
    if (tab === 'cover' || custom) {
      form.append('style_weight', adv.style_weight); form.append('weirdness_constraint', adv.weirdness_constraint);
      form.append('variety', adv.variety);
      if (tab === 'cover') form.append('audio_weight', adv.audio_weight);
    }
    const { status: st, data } = await uploadForm('/api/utile/songs', form, tab === 'cover' ? setPct : undefined);
    setBusy(false); setPct(null);
    if (st === 201 && data.success) { await loadSongs(); startPolling(); }
    else {
      setError(data.message || 'Création impossible. Réessayez.');
      if (data.code === 'credits') setShowTop(true);
    }
  };

  const canSubmit = !busy && !runningNow && (tab === 'create'
    ? (custom ? f.title.trim() && (f.style.trim() || (f.lyrics.trim() && !f.instrumental) || f.negative_tags.trim()) : f.prompt.trim())
    : audio);

  return (
    <div className="space-y-5">
      {/* portefeuille */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-sm">
        <span className="flex items-center gap-2"><Wallet className="w-4 h-4 text-fuchsia-300" /> Solde : <b>{fmtAr(status?.balance)}</b> · une création = <b>{fmtAr(status?.song_price_ar)}</b> (2 versions)</span>
        <button onClick={() => setShowTop(v => !v)} className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-500 font-semibold hover:opacity-90 transition">Recharger</button>
      </div>
      {showTop && status && <TopUp status={status} onDone={() => reload()} />}

      {/* onglets */}
      <div className="grid grid-cols-2 gap-2">
        {[['create', 'Créer une chanson', Wand2], ['cover', 'Transformer une chanson', Mic]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => { setTab(k); setError(''); }}
            className={`py-2.5 rounded-xl border text-sm font-semibold transition flex items-center justify-center gap-2 ${tab === k ? 'bg-fuchsia-500/25 border-fuchsia-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="rounded-3xl bg-white/5 border border-white/10 p-5 space-y-4">
        {tab === 'create' && (
          <div className="flex gap-2 text-sm">
            {[[false, 'Idée simple'], [true, 'Personnalisée']].map(([v, l]) => (
              <button key={l} onClick={() => setCustom(v)} className={`px-4 py-1.5 rounded-full border transition ${custom === v ? 'bg-white/15 border-white/40' : 'border-white/10 text-gray-300 hover:bg-white/10'}`}>{l}</button>
            ))}
          </div>
        )}

        {tab === 'cover' && (
          <div>
            <div onClick={() => inputRef.current?.click()} className="cursor-pointer rounded-2xl border-2 border-dashed border-white/20 bg-white/5 hover:bg-white/10 p-5 text-center transition">
              <input ref={inputRef} type="file" accept="audio/*,video/*,.mp3,.wav,.ogg,.m4a,.flac,.aac,.mp4" className="hidden"
                onChange={(e) => { const x = e.target.files?.[0]; e.target.value = ''; if (x && x.size > 100 * 1048576) setError('Fichier trop volumineux (maximum 100 Mo).'); else { setError(''); setAudio(x || null); } }} />
              {audio ? (
                <div className="flex flex-col items-center gap-1"><FileAudio className="w-8 h-8 text-fuchsia-300" /><p className="font-semibold break-all text-sm">{audio.name}</p><p className="text-xs text-blue-200">{fmtSize(audio.size)} — cliquez pour changer</p></div>
              ) : (
                <div className="flex flex-col items-center gap-1"><Upload className="w-8 h-8 text-fuchsia-300" /><p className="font-semibold text-sm">Choisissez la chanson à transformer</p><p className="text-xs text-gray-400">De 6 secondes à 8 minutes · mp3, wav, m4a, mp4…</p></div>
              )}
            </div>
            <p className="text-[11px] text-gray-400 mt-2">TeckiA garde la mélodie et la refait dans le style, avec les paroles et la voix de votre choix.</p>
          </div>
        )}

        {tab === 'create' && !custom ? (
          <div>
            <label className="text-xs text-blue-200">Décrivez votre chanson</label>
            <textarea value={f.prompt} onChange={(e) => set('prompt', e.target.value.slice(0, 3000))} rows={4}
              placeholder="Ex : une chanson joyeuse sur le marché d'Antananarivo, voix féminine, rythme salegy"
              className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm placeholder-gray-500 focus:outline-none focus:border-fuchsia-400" />
          </div>
        ) : (
          <>
            <div>
              <label className="text-xs text-blue-200">Titre {tab === 'cover' && '(facultatif)'}</label>
              <input value={f.title} onChange={(e) => set('title', e.target.value.slice(0, 80))} placeholder="Titre de la chanson"
                className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm placeholder-gray-500 focus:outline-none focus:border-fuchsia-400" />
            </div>
            <div>
              <label className="text-xs text-blue-200">Style musical</label>
              <input value={f.style} onChange={(e) => set('style', e.target.value.slice(0, 1000))} placeholder="Ex : pop afro tropical, joyeux, guitare acoustique"
                className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm placeholder-gray-500 focus:outline-none focus:border-fuchsia-400" />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {STYLE_CHIPS.map(c => (
                  <button key={c} onClick={() => set('style', f.style ? `${f.style}, ${c}` : c)} className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs transition">+ {c}</button>
                ))}
              </div>
            </div>
            {!f.instrumental && (
              <div>
                <label className="text-xs text-blue-200">Paroles {tab === 'cover' ? '(facultatif : sinon celles de la chanson d\'origine)' : '(facultatif : sinon générées)'}</label>
                <textarea value={f.lyrics} onChange={(e) => set('lyrics', e.target.value.slice(0, 5000))} rows={6}
                  placeholder={'[Couplet]\nVos paroles ici…\n[Refrain]\n…'}
                  className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm placeholder-gray-500 focus:outline-none focus:border-fuchsia-400 font-mono" />
                <p className="text-[11px] text-gray-400">{f.lyrics.length} / 5000 · utilisez des paroles originales (les textes protégés sont refusés).</p>
                <p className="text-[11px] text-fuchsia-200/80">Duo ou plusieurs voix : choisissez « Duo » et indiquez qui chante dans vos paroles, par exemple <span className="font-mono">[Female Vocal]</span> puis <span className="font-mono">[Male Vocal]</span>, ou <span className="font-mono">[Duet]</span> pour chanter ensemble. Le résultat n'est pas garanti à 100 %.</p>
              </div>
            )}
          </>
        )}

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={f.instrumental} onChange={(e) => set('instrumental', e.target.checked)} className="accent-fuchsia-400" /> Instrumental (sans voix)</label>
          {!f.instrumental && (
            <label className="flex items-center gap-2">Voix :
              <select value={f.vocal_gender} onChange={(e) => set('vocal_gender', e.target.value)} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1">
                <option value="">Au choix de l'IA</option><option value="f">Féminine</option><option value="m">Masculine</option><option value="duo">Duo (homme + femme)</option>
              </select>
            </label>
          )}
          <label className="flex items-center gap-2">Qualité :
            <select value={f.model} onChange={(e) => set('model', e.target.value)} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1">
              {(status?.song_models || []).map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </label>
        </div>

        {(tab === 'cover' || custom) && (
          <div>
            <button onClick={() => setShowAdv(v => !v)} className="text-sm text-fuchsia-300 flex items-center gap-1.5"><ChevronDown className={`w-4 h-4 transition ${showAdv ? 'rotate-180' : ''}`} /> Réglages avancés</button>
            {showAdv && (
              <div className="mt-3 space-y-4">
                <div>
                  <label className="text-xs text-blue-200">Styles à éviter (facultatif)</label>
                  <input value={f.negative_tags} onChange={(e) => set('negative_tags', e.target.value.slice(0, 300))} placeholder="Ex : électro, voix aiguë"
                    className="w-full mt-1 bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm placeholder-gray-500 focus:outline-none focus:border-fuchsia-400" />
                </div>
                <Slider label="Fidélité au style demandé" hint="Plus c'est haut, plus le style indiqué est respecté." value={adv.style_weight} onChange={(v) => setAdv(a => ({ ...a, style_weight: v }))} />
                <Slider label="Créativité" hint="Plus c'est haut, plus le résultat est original et surprenant." value={adv.weirdness_constraint} onChange={(v) => setAdv(a => ({ ...a, weirdness_constraint: v }))} />
                {tab === 'cover' && <Slider label="Fidélité à la chanson d'origine" hint="Plus c'est haut, plus la nouvelle version ressemble à l'originale." value={adv.audio_weight} onChange={(v) => setAdv(a => ({ ...a, audio_weight: v }))} />}
                <label className="flex items-center gap-2 text-sm">Diversité des versions :
                  <select value={adv.variety} onChange={(e) => setAdv(a => ({ ...a, variety: parseInt(e.target.value, 10) }))} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1">
                    <option value={0}>Aucune</option><option value={1}>Normale</option><option value={2}>Élevée</option><option value={3}>Très élevée</option><option value={4}>Maximale</option>
                  </select>
                </label>
              </div>
            )}
          </div>
        )}

        {pct !== null && <p className="text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Envoi de la chanson… {pct}%</p>}
        {error && <p className="text-rose-300 text-sm bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3">{error}</p>}

        <button onClick={insufficient ? () => { setShowTop(true); setError(`Solde insuffisant : une création coûte ${fmtAr(status.song_price_ar)}. Rechargez votre portefeuille.`); } : submit}
          disabled={insufficient ? false : !canSubmit}
          className="w-full py-3.5 rounded-2xl font-bold bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:opacity-90 disabled:opacity-40 transition flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Music className="w-5 h-5" />}
          {insufficient ? 'Recharger pour créer' : runningNow ? 'Une création est en cours…' : `${tab === 'create' ? 'Créer la chanson' : 'Transformer la chanson'} — ${fmtAr(status?.song_price_ar)}`}
        </button>
        <p className="text-[11px] text-gray-400 text-center">Si la création échoue, votre solde est remboursé automatiquement.</p>
      </div>

      {/* historique */}
      {songs.length > 0 && (
        <div className="space-y-3">
          <p className="font-bold">Mes chansons</p>
          {songs.map(s => <SongCard key={s.id} song={s} />)}
        </div>
      )}
    </div>
  );
}
